import React from "react";
import { ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";

const CONTENT = {
  en: {
    title: "Privacy Policy",
    updated: "Last updated: June 2026",
    sections: [
      ["Who we are", "Suppev is an educational platform that summarizes scientific evidence on dietary supplements. This policy explains what data we collect and how we use it. Suppev does not diagnose, treat, prevent or cure any disease."],
      ["Data we collect", "Account data (name, email, hashed password), your language preference, supplement scans you upload, and subscription status. Payments are processed by Stripe; we never store your card details."],
      ["How we use your data", "To provide and secure your account, run label analysis and PubMed evidence summaries, manage your Premium subscription, and improve the product. We do not sell your personal data."],
      ["Cookies", "We use essential cookies to keep you signed in and remember your language. With your consent we may use analytics cookies to understand usage. You can change your choice anytime via the cookie banner."],
      ["Third parties", "We rely on Stripe (payments), OpenAI (AI analysis) and the NCBI/PubMed API (research). Data shared is limited to what each service needs to function."],
      ["Your rights", "You may access, correct or delete your account data. Contact us to exercise these rights or ask questions."],
      ["Contact", "For privacy requests, email privacy@suppev.com."],
    ],
  },
  es: {
    title: "Política de Privacidad",
    updated: "Última actualización: junio de 2026",
    sections: [
      ["Quiénes somos", "Suppev es una plataforma educativa que resume la evidencia científica sobre suplementos alimenticios. Esta política explica qué datos recopilamos y cómo los usamos. Suppev no diagnostica, trata, previene ni cura ninguna enfermedad."],
      ["Datos que recopilamos", "Datos de la cuenta (nombre, correo, contraseña cifrada), tu preferencia de idioma, las etiquetas que escaneas y el estado de tu suscripción. Los pagos los procesa Stripe; nunca almacenamos los datos de tu tarjeta."],
      ["Cómo usamos tus datos", "Para ofrecer y proteger tu cuenta, analizar etiquetas y resumir la evidencia de PubMed, gestionar tu suscripción Premium y mejorar el producto. No vendemos tus datos personales."],
      ["Cookies", "Usamos cookies esenciales para mantener tu sesión y recordar tu idioma. Con tu consentimiento podemos usar cookies analíticas para entender el uso. Puedes cambiar tu elección en cualquier momento desde el banner de cookies."],
      ["Terceros", "Nos apoyamos en Stripe (pagos), OpenAI (análisis con IA) y la API de NCBI/PubMed (investigación). Los datos compartidos se limitan a lo que cada servicio necesita para funcionar."],
      ["Tus derechos", "Puedes acceder, corregir o eliminar los datos de tu cuenta. Contáctanos para ejercer estos derechos o resolver dudas."],
      ["Contacto", "Para solicitudes de privacidad, escribe a privacy@suppev.com."],
    ],
  },
};

export default function Privacy() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.en;
  return (
    <div className="mx-auto max-w-3xl" data-testid="privacy-page">
      <div className="mb-8 flex items-center gap-3">
        <Logo size={52} />
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.title}</h1>
          <p className="text-sm text-gray-400">{c.updated}</p>
        </div>
      </div>
      <div className="mb-6 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
        <ShieldCheck size={18} className="mt-0.5 shrink-0" />
        <p>Your data is encrypted in transit. Payments are handled by Stripe — we never see your card number.</p>
      </div>
      <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-8">
        {c.sections.map(([h, body], i) => (
          <section key={i}>
            <h2 className="mb-1.5 text-lg font-bold text-gray-800">{h}</h2>
            <p className="text-sm leading-relaxed text-gray-600">{body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
